import {
  Chat,
  Message as ChatMessage,
  CitationChunkPayload,
  SourceMetadata,
  SourceOrigin,
} from "@/types/assistantChat"
import { ApiCitation, ApiRetrievedReference } from "@/types/questionnaire/api"
import { Citation } from "@/types/questionnaire/models"
import { stripPreprocessedSuffix } from "@/utils/fileUtils"

export interface ChatSource {
  url: string
  title: string
}

export const UNPERSISTED_CHAT_ID = "1"

export function isPersistedConversationId(
  conversationId: string | undefined | null,
): conversationId is string {
  return Boolean(conversationId && conversationId !== UNPERSISTED_CHAT_ID)
}

/**
 * Resolve a human-readable title for a chat session.
 *
 * Prefers the persisted `chat.title`. When that is empty (e.g. an in-memory
 * conversation whose title hasn't been hydrated), derives a title from the
 * first user message, truncated to a sensible length. Falls back to the
 * provided generic label only when there is no usable content at all.
 */
export function resolveChatTitle(
  chat: Chat,
  fallbackLabel: () => string,
  maxLength = 80,
): string {
  const persisted = chat.title?.trim()
  if (persisted) {
    return persisted
  }

  const firstUserMessage = chat.messages?.find(
    (message) => message.role === "user" && message.content.trim(),
  )
  const derived = firstUserMessage?.content.trim().replace(/\s+/g, " ")
  if (derived) {
    return derived.length > maxLength
      ? `${derived.slice(0, maxLength).trimEnd()}…`
      : derived
  }

  return fallbackLabel()
}

/**
 * Convert chat messages to plain text format
 */
export function convertChatMessagesToTextFormat(chat: Chat): string {
  return (
    chat.messages
      ?.map((message) => {
        return `${message.role}: ${message.content}`
      })
      .join("\n") ?? ""
  )
}

export function extractUniqueCitations(
  message: ChatMessage,
  role: string,
  unknownSourceLabel: string,
): Citation[] {
  const hasCitations = message.citations && message.citations.length > 0

  if (!hasCitations || role === "user") {
    return []
  }

  return buildNormalizedCitations(message, unknownSourceLabel)
}

/**
 * Extract unique sources from a message's citations
 */
export function extractUniqueSources(
  message: ChatMessage,
  role: string,
): ChatSource[] {
  const hasCitations = message.citations && message.citations.length > 0

  if (!hasCitations || role === "user") {
    return []
  }

  const sources = message.citations!.flatMap((citation) =>
    (citation.retrievedReferences || []).map((ref: ApiRetrievedReference) => {
      const url =
        ref.metadata?.url ||
        ref.metadata?.["x-amz-bedrock-kb-source-uri"] ||
        ref.location?.url ||
        ref.location?.s3Location?.uri ||
        "#"

      return {
        url,
        title: stripPreprocessedSuffix(
          ref.metadata?.title ||
            ref.location?.s3Location?.uri?.split("/").pop() ||
            extractDisplayNameFromUrl(url) ||
            ref.metadata?.documentId ||
            "Source",
        ),
      }
    }),
  )

  // Remove duplicates based on URL
  return sources.filter(
    (source, idx, self) => self.findIndex((s) => s.url === source.url) === idx,
  )
}

/**
 * Extract unique citations from streaming message citations
 */
export function extractStreamingMessageCitations(
  message: ChatMessage,
  unknownSourceLabel: string,
): Citation[] {
  if (!message.citations || message.citations.length === 0) {
    return []
  }

  return buildNormalizedCitations(message, unknownSourceLabel)
}

/**
 * Extract unique sources from streaming message citations
 */
export function extractStreamingMessageSources(
  message: ChatMessage,
): ChatSource[] {
  if (!message.citations || message.citations.length === 0) {
    return []
  }

  const sources = message.citations
    .flatMap((citation) => {
      return (citation.retrievedReferences || []).map(
        (ref: ApiRetrievedReference) => {
          const url =
            ref.metadata?.url ||
            ref.metadata?.["x-amz-bedrock-kb-source-uri"] ||
            ref.location?.url ||
            ref.location?.s3Location?.uri ||
            "#"
          return {
            url,
            title: stripPreprocessedSuffix(
              ref.metadata?.title ||
                ref.location?.s3Location?.uri?.split("/").pop() ||
                extractDisplayNameFromUrl(url) ||
                ref.metadata?.documentId ||
                "Source",
            ),
          }
        },
      )
    })
    .filter(
      (source, idx, self) =>
        self.findIndex((s) => s.url === source.url) === idx,
    )

  return sources
}

/**
 * Create an empty chat object
 */
export function createEmptyChat(): Chat {
  return {
    id: UNPERSISTED_CHAT_ID,
    title: "",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    messages: [],
    webSearchEnabled: false,
  }
}

export function summarizeCitationOrigins(citations: ApiCitation[] = []): {
  rag: number
  web: number
  mc: number
} {
  return citations.reduce(
    (acc, citation) => {
      ;(citation.retrievedReferences || []).forEach((ref) => {
        const origin =
          ref.origin ?? ref.metadata?.sourceType ?? ref.metadata?.source_type
        if (origin === "web") {
          acc.web += 1
        } else if (origin === "mc-data") {
          acc.mc += 1
        } else {
          acc.rag += 1
        }
      })
      return acc
    },
    { rag: 0, web: 0, mc: 0 },
  )
}

export function determineSourceOrigin(
  citations: ApiCitation[] = [],
  metadata?: SourceMetadata,
): SourceOrigin {
  const metadataBreakdown = metadata
    ? {
        rag: metadata.workspaceResultsCount,
        web: metadata.webResultsCount,
        mc: 0,
      }
    : null

  const metadataIsAllZero =
    metadataBreakdown !== null &&
    metadataBreakdown.rag === 0 &&
    metadataBreakdown.web === 0 &&
    metadataBreakdown.mc === 0

  const breakdown =
    metadataBreakdown && !metadataIsAllZero
      ? metadataBreakdown
      : summarizeCitationOrigins(citations)

  const activeOriginCount = [
    breakdown.web > 0,
    breakdown.rag > 0,
    breakdown.mc > 0,
  ].filter(Boolean).length

  if (activeOriginCount > 1) {
    return "mixed"
  }
  if (breakdown.web > 0) {
    return "web"
  }
  if (breakdown.mc > 0) {
    return "mc-data"
  }
  if (breakdown.rag > 0) {
    return "rag"
  }
  return "none"
}

export function isCitationPayload(
  content: string | ApiCitation[] | CitationChunkPayload,
): content is CitationChunkPayload {
  return (
    typeof content !== "string" &&
    (content as CitationChunkPayload).citations !== undefined
  )
}

function buildNormalizedCitations(
  message: ChatMessage,
  unknownSourceLabel: string,
): Citation[] {
  if (!message.citations || message.citations.length === 0) {
    return []
  }

  return normalizeCitations(message.citations, unknownSourceLabel)
}

/** API citations -> the de-duplicated display citations a source list shows. */
export function normalizeCitations(
  citations: ApiCitation[],
  unknownSourceLabel: string,
): Citation[] {
  const normalized = collectNormalizedReferences(citations, unknownSourceLabel)
  const seen = new Set<string>()

  return normalized.reduce<Citation[]>((acc, entry) => {
    if (seen.has(entry.key)) {
      return acc
    }
    seen.add(entry.key)
    acc.push(entry.citation)
    return acc
  }, [])
}

function collectNormalizedReferences(
  citations: ApiCitation[],
  unknownSourceLabel: string,
): Array<{ citation: Citation; key: string }> {
  let fallbackCounter = 0

  return citations.flatMap((citation) =>
    (citation.retrievedReferences || []).map((ref: ApiRetrievedReference) =>
      normalizeReference(ref, fallbackCounter++, unknownSourceLabel),
    ),
  )
}

function normalizeReference(
  reference: ApiRetrievedReference,
  counter: number,
  unknownSourceLabel: string,
): { citation: Citation; key: string } {
  const url =
    reference.metadata?.url ||
    reference.metadata?.["x-amz-bedrock-kb-source-uri"] ||
    reference.location?.url ||
    reference.location?.s3Location?.uri

  const sourceFilePage =
    reference.metadata?.["x-amz-bedrock-kb-document-page-number"] || 1
  const sourceFileId =
    reference.metadata?.document_id ||
    reference.metadata?.documentId ||
    reference.metadata?.["x-amz-bedrock-kb-source-uri"] ||
    url ||
    `reference-${counter}`

  const sourceFileName = stripPreprocessedSuffix(
    reference.metadata?.title ||
      reference.location?.s3Location?.uri?.split("/").pop() ||
      extractDisplayNameFromUrl(url) ||
      reference.metadata?.documentId ||
      unknownSourceLabel,
  )

  const citation: Citation = {
    sourceFileName,
    sourceFileId,
    sourceFilePage,
    sourceText: reference.content || "",
    sourceType:
      reference.origin ??
      reference.metadata?.sourceType ??
      reference.metadata?.source_type,
    url,
    toolName: reference.metadata?.tool,
  }

  const key = `${sourceFileId}-${sourceFilePage}-${url ?? sourceFileName}`
  return { citation, key }
}

function extractDisplayNameFromUrl(url?: string): string | undefined {
  if (!url) {
    return undefined
  }
  const sanitized = url.split(/[?#]/)[0]
  const parts = sanitized.split("/").filter(Boolean)
  return parts.pop()
}
